// Adapted from eval/dashboard/internal_web/src/components/ui/Select.tsx (ATM-57): field tone only, the floating-panel chrome inlined. Keep in step until ATM-60 shares it.
import * as RadixSelect from "@radix-ui/react-select";
import { Check, ChevronDown } from "lucide-react";

import { cn } from "../../lib/cn";

/** The chrome a floating menu shares with the eval dashboard's: surface, hairline, radius, shadow. */
const FLOATING_PANEL = "z-50 rounded-panel border border-hairline bg-surface shadow-float";

/** One choice. `hint` shows to the right. */
export type SelectOption<T extends string> = {
  value: T;
  label: string;
  hint?: string;
};

type Props<T extends string> = {
  value: T;
  onChange: (value: T) => void;
  options: readonly SelectOption<T>[];
  /** Always supplied: the trigger is icon-light, so the name is the only cue. */
  label: string;
  /** Render the label above the control instead of only for screen readers. */
  showLabel?: boolean;
  className?: string;
};

/**
 * A single-choice menu over a closed union — the eval dashboard's `Select` (field tone).
 *
 * Radix Select rather than a native `<select>` so the menu matches the app's own chrome.
 * Radix reserves the empty string, so an "all" choice needs a real value of its own.
 */
export default function Select<T extends string>({
  value,
  onChange,
  options,
  label,
  showLabel = false,
  className,
}: Props<T>) {
  const selected = options.find((option) => option.value === value);
  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      <span className={showLabel ? "text-cell font-medium text-ink-3" : "sr-only"}>{label}</span>
      <RadixSelect.Root value={value} onValueChange={(next) => onChange(next as T)}>
        <RadixSelect.Trigger
          aria-label={label}
          className="control flex h-10 items-center gap-2 px-3 text-cell text-ink"
        >
          <RadixSelect.Value className="truncate">{selected?.label ?? value}</RadixSelect.Value>
          <RadixSelect.Icon className="ml-auto">
            <ChevronDown size={18} className="shrink-0 text-ink-3" aria-hidden />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>
        <RadixSelect.Portal>
          <RadixSelect.Content
            position="popper"
            sideOffset={6}
            className={cn(FLOATING_PANEL, "max-h-[22rem] min-w-[12rem] overflow-hidden")}
          >
            <RadixSelect.Viewport className="p-1.5">
              {options.map((option) => (
                <RadixSelect.Item
                  key={option.value}
                  value={option.value}
                  className={cn(
                    "flex h-9 cursor-default select-none items-center gap-2 rounded-control px-2 text-cell text-ink outline-none",
                    "data-[highlighted]:bg-surface-muted",
                  )}
                >
                  <span className="grid w-3.5 place-items-center">
                    <RadixSelect.ItemIndicator>
                      <Check size={13} aria-hidden />
                    </RadixSelect.ItemIndicator>
                  </span>
                  <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                  {option.hint !== undefined ? (
                    <span className="ml-auto pl-3 text-xs text-ink-muted">{option.hint}</span>
                  ) : null}
                </RadixSelect.Item>
              ))}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    </div>
  );
}
